import React, { useCallback, useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createSchedule,
  updateSchedule,
  getScheduleById,
  getRoomsDropdown,
  getActiveGames,
  searchCatalogCards,
} from "../../api/adminApi";
import { getActiveEmployee } from "../../api/EmployeeApi";
import { getPrivateGamesIds } from "../../api/ScheduleApi";
import { getDayOff, getOccupiedSlotsOfDay, getSlotTemplates } from "../../api/SlotsApi";
import common from "../../common.module.scss";
import { FaArrowLeft } from "react-icons/fa";
import { GoPencil } from "react-icons/go";
import { toast } from "react-toastify";
import { EmployeeContext } from "@/pages/adminPanel/context/EmployeeContext.jsx";

/* ────────────────────────────────────────────────────────────── */
/*                    Вспомогательные функции                     */
/* ────────────────────────────────────────────────────────────── */
const getInitialFormState = ({ startedAt, countPlayers }) => {
  const date = startedAt ? startedAt.split("T")[0] : "";
  const time = startedAt ? startedAt.split("T")[1]?.slice(0, 5) : "";
  return {
    scheduleId: "",
    gameCardId: "",
    gameMasterId: "",
    gameRoomId: "",
    minDurationHours: 4,
    maxDurationHours: "",
    maxCountPlayers: countPlayers,
    currentCountPlayers: 0,
    startedDate: date,
    startedTime: time,
    isPin: false,
    isDiscount: false,
    discount: 0,
  };
};

/* ────────────────────────────────────────────────────────────── */
/*                             Компонент                           */
/* ────────────────────────────────────────────────────────────── */
export default function ScheduleForm() {
  const { employee } = useContext(EmployeeContext);
  const role = employee?.role;

  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const startedAtParam = searchParams.get("startedAt");
  const isPrivateParam = searchParams.get("private") === "true";
  const requestIdParam = searchParams.get("id");
  const countPlayersParam = Number(searchParams.get("countPlayers")) || 5;

  /* ── state ─────────────────────────────────────────────────── */
  const [games, setGames] = useState([]);
  const [masters, setMasters] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isTimeReady, setIsTimeReady] = useState(false);

  const [isPrivate, setIsPrivate] = useState(isPrivateParam);
  const [requestId, setRequestId] = useState(requestIdParam || "");
  const [accessType, setAccessType] = useState("CLOSE");

  // combobox state
  const [gameSearch, setGameSearch] = useState("");
  const [selectedGameTitle, setSelectedGameTitle] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const [form, setForm] = useState(
    getInitialFormState({ startedAt: startedAtParam, countPlayers: countPlayersParam })
  );

  // SLOTS
  const [dayInfo, setDayInfo] = useState(null);
  const [slotTemplates, setSlotTemplates] = useState([]);
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [selectedSlotTemplateId, setSelectedSlotTemplateId] = useState("");
  const [originSlotTemplateId, setOriginSlotTemplateId] = useState("");

  const abortControllerRef = useRef(null);
  const comboRef = useRef(null);

  const buildReturnUrl = () => `/admin?${searchParams.toString()}`;

  /* ────────────────────────────────────────────────────────────── */
  /*                  Загрузка информации о дне                     */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!form.startedDate) return;

    const fetchData = async () => {
      try {
        const res = await getDayOff(form.startedDate);
        setDayInfo(res.data);
      } catch (error) {
        console.error("Ошибка:", error);
        toast.error("Ошибка проверки календаря");
      }
    };

    fetchData();
  }, [form.startedDate]);

  /* ────────────────────────────────────────────────────────────── */
  /*              Загрузка шаблонов слотов при выходном             */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!dayInfo?.dayOff) return;
    getSlotTemplates()
      .then((r) => {
        const active = r.data.filter((s) => s.isActive);
        setSlotTemplates(active);
      })
      .catch(() => toast.error("Ошибка загрузки слотов"));
  }, [dayInfo]);

  /* ────────────────────────────────────────────────────────────── */
  /*             Загрузка занятых слотов при выходном               */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!dayInfo?.dayOff || !form.startedDate) return;
    getOccupiedSlotsOfDay({
      startDate: form.startedDate,
      endDate: form.startedDate,
      roomId: form.gameRoomId || undefined,
    })
      .then((r) => setOccupiedSlots(r.data))
      .catch(() => toast.error("Ошибка загрузки занятых слотов"));
  }, [dayInfo, form.startedDate, form.gameRoomId]);

  /* ────────────────────────────────────────────────────────────── */
  /*              Запрос на свободные залы при изменении            */
  /*              (выходной: слот ИЛИ время)                         */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    // ✅ выходной: разрешаем либо слот, либо время
    if (dayInfo?.dayOff && !selectedSlotTemplateId && !form.startedTime) {
      setRooms([]);
      setIsTimeReady(false);
      return;
    }

    // ✅ будни: обязаны дата+время
    if (!dayInfo?.dayOff && (!form.startedDate || !form.startedTime)) {
      setRooms([]);
      setIsTimeReady(false);
      return;
    }

    // на всякий: дата нужна всегда
    if (!form.startedDate) {
      setRooms([]);
      setIsTimeReady(false);
      return;
    }

    let startedAt = "";

    if (dayInfo?.dayOff) {
      // ✅ если выбран слот — берём его
      if (selectedSlotTemplateId) {
        const slot = slotTemplates.find((s) => s.id === Number(selectedSlotTemplateId));
        if (!slot) {
          setRooms([]);
          setIsTimeReady(false);
          return;
        }
        startedAt = `${form.startedDate}T${slot.startTime}`;
      }
      // ✅ иначе — если введено время, используем время
      else if (form.startedTime) {
        startedAt = `${form.startedDate}T${form.startedTime}`;
      } else {
        setRooms([]);
        setIsTimeReady(false);
        return;
      }
    } else {
      startedAt = `${form.startedDate}T${form.startedTime}`;
    }

    if (!startedAt || !form.minDurationHours) {
      setRooms([]);
      setIsTimeReady(false);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const ac = new AbortController();
    abortControllerRef.current = ac;

    const scheduleId = isEdit ? form.scheduleId : undefined;

    const t = setTimeout(() => {
      getRoomsDropdown(
        {
          startAt: startedAt,
          minDurationHours: Number(form.minDurationHours),
          maxDurationHours: form.maxDurationHours ? Number(form.maxDurationHours) : null,
          scheduleId,
        },
        { signal: ac.signal }
      )
        .then((r) => setRooms(r.data.content || r.data))
        .then(() => setIsTimeReady(true))
        .catch((err) => {
          if (err.name !== "AbortError") toast.error("Ошибка при загрузке залов");
        });
    }, 300);

    return () => {
      clearTimeout(t);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [
    dayInfo,
    form.startedDate,
    form.startedTime,
    selectedSlotTemplateId,
    slotTemplates,
    form.minDurationHours,
    form.maxDurationHours,
    isEdit,
    form.scheduleId,
  ]);

  /* ────────────────────────────────────────────────────────────── */
  /*                 Поиск открытых игр                             */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (isPrivate) return;
    const q = gameSearch.trim();

    if (q.length < 2) {
      setIsSearching(false);
      getActiveGames()
        .then((r) => {
          const data = r.data.content || r.data || [];
          setGames(data);
          setIsOpen(true);
          setHighlightedIndex(-1);
        })
        .catch(() => toast.error("Ошибка загрузки игр"));
      return;
    }

    let ignore = false;
    setIsSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await searchCatalogCards(q);
        if (!ignore) {
          setGames(data || []);
          setIsOpen(true);
          setHighlightedIndex(-1);
          setForm((prev) => ({
            ...prev,
            gameCardId: (data || []).some((d) => String(d.id) === String(prev.gameCardId))
              ? prev.gameCardId
              : "",
          }));
        }
      } catch {
        if (!ignore) toast.error("Ошибка поиска игр");
      } finally {
        if (!ignore) setIsSearching(false);
      }
    }, 300);

    return () => {
      ignore = true;
      clearTimeout(t);
    };
  }, [gameSearch, isPrivate]);

  /* ────────────────────────────────────────────────────────────── */
  /*              Закрыть список по клику вне                       */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    function onDocClick(e) {
      if (!comboRef.current) return;
      if (!comboRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  /* ────────────────────────────────────────────────────────────── */
  /*                            Masters                              */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const emp = await getActiveEmployee();
        const onlyMasters = (emp.data.content || emp.data).filter((e) => e.role.includes("MASTER"));
        setMasters(onlyMasters);
      } catch {
        toast.error("Ошибка при загрузке мастеров");
      }
    })();
  }, []);

  /* ────────────────────────────────────────────────────────────── */
  /*                       Private Games                            */
  /* ────────────────────────────────────────────────────────────── */
  const loadPrivateGames = useCallback(async () => {
    try {
      let res;
      if (isEdit) {
        const { data: s } = await getScheduleById(id);
        res = await getPrivateGamesIds(s?.privateGameRequest?.id);
      } else {
        res = await getPrivateGamesIds();
      }
      const rows = res.data.content || res.data;
      const mapped = rows.map((r) => ({
        id: String(r.privateGameRequest?.id ?? r.id),
        title: r.privateGameRequest?.catalogCard?.title ?? r.title ?? "Закрытая игра",
      }));
      setGames(mapped);
      setRequestId((prev) => (prev && mapped.some((m) => m.id === prev) ? prev : mapped[0]?.id || ""));
    } catch {
      toast.error("Ошибка при загрузке заявок");
    }
  }, [id, isEdit]);

  /* ────────────────────────────────────────────────────────────── */
  /*                  Переключение private ↔ open                   */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (isPrivate) {
      loadPrivateGames();
      setIsOpen(false);
    } else {
      getActiveGames()
        .then((g) => setGames(g.data.content || g.data))
        .catch(() => toast.error("Ошибка загрузки игр"));
      setRequestId("");
    }
    setGameSearch("");
    setHighlightedIndex(-1);
  }, [isPrivate, loadPrivateGames]);

  /* ────────────────────────────────────────────────────────────── */
  /*                   Редактирование расписания                    */
  /* ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;

    (async () => {
      try {
        const { data: s } = await getScheduleById(id);

        setIsPrivate(Boolean(s.isClosed));
        setRequestId(s.isClosed ? String(s.privateGameRequest?.id) : s.catalogCard?.id);
        setAccessType(s.accessType || "CLOSE");

        const date = s.startedAt ? s.startedAt.split("T")[0] : "";

        setForm({
          scheduleId: s.id,
          gameCardId: String(s.catalogCard?.id || ""),
          gameMasterId: s.employee?.id || "",
          gameRoomId: s.gameRoom?.id || "",
          minDurationHours: s.minDurationHours,
          maxDurationHours: s.maxDurationHours || "",
          maxCountPlayers: s.maxCountPlayers,
          currentCountPlayers: s.currentCountPlayers,
          startedDate: date,
          startedTime: s.startedAt && !s.slotTemplateId ? s.startedAt.split("T")[1]?.slice(0, 5) : "",
          isPin: s.isPin,
          isDiscount: s.discount > 0,
          discount: s.discount || 0,
        });

        if (s.slotTemplateId) {
          setSelectedSlotTemplateId(String(s.slotTemplateId));
          setOriginSlotTemplateId(String(s.slotTemplateId));
          // ✅ если у редактируемой игры есть слот — время не нужно
          setForm((prev) => ({ ...prev, startedTime: "" }));
        }

        if (!s.isClosed && s.catalogCard?.title) {
          setSelectedGameTitle(s.catalogCard.title);
          setGameSearch("");
        }

        if (s.isClosed) {
          loadPrivateGames();
        } else {
          const g = await getActiveGames();
          const list = g.data.content || g.data || [];
          setGames(list);

          if (s.catalogCard?.id && s.catalogCard?.title) {
            const exists = list.some((x) => String(x.id) === String(s.catalogCard.id));
            if (!exists) {
              setGames((prev) => [{ id: s.catalogCard.id, title: s.catalogCard.title }, ...prev]);
            }
          }
        }
      } catch (e) {
        toast.error("Не удалось загрузить расписание");
        console.error(e);
      }
    })();
  }, [id, isEdit, loadPrivateGames]);

  /* ────────────────────────────────────────────────────────────── */
  /*                        Обработчики                             */
  /* ────────────────────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDiscountToggle = (checked) => {
    setForm((prev) => ({
      ...prev,
      isDiscount: checked,
      discount: checked ? (prev.discount === 0 ? 1 : prev.discount) : 0,
    }));
  };

  const handleDiscountChange = (value) => {
    if (value === "") {
      setForm((prev) => ({ ...prev, discount: "" }));
      return;
    }
    if (!/^\d+$/.test(value)) return;
    setForm((prev) => ({ ...prev, discount: Number(value) }));
  };

  const handleGameTypeChange = (e) => {
    const type = e.target.value;
    if (type === "PRIVATE") {
      setIsPrivate(true);
      setAccessType("CLOSE");
      setForm((prevState) => ({ ...prevState, gameCardId: "" }));
      return;
    }
    setIsPrivate(false);
    setAccessType(type);
  };

  const selectGame = (game) => {
    setForm((prev) => ({ ...prev, gameCardId: String(game.id) }));
    setSelectedGameTitle(game.title || "");
    setGameSearch("");
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const onComboKeyDown = (e) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev + 1;
        return next >= games.length ? games.length - 1 : next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? 0 : next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < games.length) selectGame(games[highlightedIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [games]);

  const handleSubmit = async () => {
    // ✅ выходной: слот ИЛИ время
    if (dayInfo?.dayOff === true && !selectedSlotTemplateId && !form.startedTime) {
      toast.error("Выберите слот или укажите время");
      return;
    }

    // ✅ будни: дата+время
    if (dayInfo?.dayOff === false && (!form.startedDate || !form.startedTime)) {
      toast.error("Укажите дату и время");
      return;
    }

    const discountNormalized = form.isDiscount
      ? Math.min(100, Math.max(1, Number(form.discount || 1)))
      : 0;

    const payload = {
      ...form,
      gameCardId: isPrivate ? null : form.gameCardId,
      privateGameRequestId: isPrivate ? requestId : null,
      isClosed: isPrivate,
      accessType,
      minDurationHours: Number(form.minDurationHours),
      maxDurationHours: form.maxDurationHours ? Number(form.maxDurationHours) : null,
      maxCountPlayers: Number(form.maxCountPlayers),
      discount: discountNormalized,
    };

    // ✅ если слот выбран — используем slotTemplateId и убираем startedTime
    if (dayInfo?.dayOff && selectedSlotTemplateId) {
      payload.slotTemplateId = Number(selectedSlotTemplateId);
      delete payload.startedTime;
    } else {
      delete payload.slotTemplateId;
    }

    try {
      if (isEdit) await updateSchedule(id, payload);
      else await createSchedule(payload);
      navigate(buildReturnUrl());
    } catch (e) {
      toast.error(e?.response?.data?.message || "Ошибка при сохранении");
    }
  };

  const gameTypeValue = isPrivate ? "PRIVATE" : accessType === "OPEN" ? "OPEN" : "CLOSE";

  /* ────────────────────────────────────────────────────────────── */
  /*                             Render                             */
  /* ────────────────────────────────────────────────────────────── */
  return (
    <div className={common.container + " d-flex justify-content-center align-items-center"}>
      <div className={`container ${common.container}`}>
        <div className="row">
          <span className="btn-word black btn-link col-md-auto col-2" onClick={() => navigate(buildReturnUrl())}>
            <FaArrowLeft className={common.serviceButton} />
          </span>
          <h2 className="col-md col-10">{isEdit ? "Редактирование расписания" : "Добавление игры в расписание"}</h2>
        </div>

        <div className="row mt-4">
          <div className="col-md-8">
            <div className="mb-2">
              <div className="form-check form-switch">
                <input
                  id="isDiscount"
                  type="checkbox"
                  className="form-check-input"
                  checked={form.isDiscount}
                  onChange={(e) => handleDiscountToggle(e.target.checked)}
                />
                <label className="form-check-label ms-2" htmlFor="isDiscount">
                  Игра недели
                </label>
              </div>

              {form.isDiscount && (
                <div className="mb-3">
                  <label htmlFor="discountInput" className={common.formLabel}>
                    Скидка (%)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="discountInput"
                    value={form.discount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    max="100"
                    required
                  />
                </div>
              )}
            </div>

            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                name="isPin"
                id="isPin"
                checked={form.isPin}
                onChange={handleChange}
              />
              <label className="form-check-label ms-2" htmlFor="isPin">
                Закреплено
              </label>
            </div>

            {isPrivate && (
              <select className="form-control mb-3" value={requestId} onChange={(e) => setRequestId(e.target.value)}>
                <option value="">Выберите заявку</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.title}
                  </option>
                ))}
              </select>
            )}

            {!isPrivate && (
              <div
                className="mb-3"
                ref={comboRef}
                style={{ position: "relative" }}
                aria-haspopup="listbox"
                aria-owns="games-combobox-list"
                aria-expanded={isOpen}
              >
                <input
                  type="text"
                  className="form-control"
                  placeholder="Найдите игру"
                  value={gameSearch || (form.gameCardId ? selectedGameTitle : "")}
                  onChange={(e) => {
                    const v = e.target.value;
                    setGameSearch(v);
                    if (v && v !== selectedGameTitle) setForm((prev) => ({ ...prev, gameCardId: "" }));
                    setIsOpen(true);
                  }}
                  onFocus={() => setIsOpen(true)}
                  onKeyDown={onComboKeyDown}
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="games-combobox-list"
                  aria-activedescendant={highlightedIndex >= 0 ? `games-option-${highlightedIndex}` : undefined}
                />

                {isOpen && games.length > 0 && (
                  <ul
                    id="games-combobox-list"
                    className="list-group"
                    role="listbox"
                    style={{
                      position: "absolute",
                      width: "100%",
                      zIndex: 1000,
                      maxHeight: 280,
                      overflowY: "auto",
                      boxShadow: "0 0.5rem 1rem rgba(0,0,0,.15)",
                    }}
                  >
                    {games.map((g, idx) => {
                      const isActive = idx === highlightedIndex;
                      return (
                        <li
                          id={`games-option-${idx}`}
                          key={g.id}
                          role="option"
                          aria-selected={isActive}
                          className={`list-group-item list-group-item-action ${isActive ? "active" : ""}`}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectGame(g);
                          }}
                        >
                          {g.title}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {isOpen && games.length === 0 && !isSearching && (
                  <div
                    className="list-group"
                    style={{
                      position: "absolute",
                      width: "100%",
                      zIndex: 1000,
                      boxShadow: "0 0.5rem 1rem rgba(0,0,0,.15)",
                    }}
                  >
                    <div className="list-group-item text-muted">Ничего не найдено</div>
                  </div>
                )}
              </div>
            )}

            <select className="form-control mb-3" name="gameMasterId" value={form.gameMasterId} onChange={handleChange}>
              <option value="">Мастер</option>
              {masters.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>

            <input type="date" className="form-control mb-3" name="startedDate" value={form.startedDate} onChange={handleChange} />

            {/* ✅ выходной: слот доступен, но не обязателен */}
            {dayInfo?.dayOff && (
              <select
                className="form-control mb-3"
                value={selectedSlotTemplateId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedSlotTemplateId(v);
                  // ✅ выбрали слот — чистим время, чтобы не было конфликта
                  if (v) setForm((prev) => ({ ...prev, startedTime: "" }));
                }}
              >
                <option value="">Выберите слот (или укажите время ниже)</option>
                {slotTemplates.map((slot) => {
                  const isOccupied = occupiedSlots.some((o) => o.slotTemplateId === slot.id && o.roomId === form.gameRoomId);
                  return (
                    <option
                      key={slot.id}
                      value={slot.id}
                      disabled={isOccupied && String(slot.id) !== String(originSlotTemplateId)}
                    >
                      {slot.startTime} – {slot.endTime}
                    </option>
                  );
                })}
              </select>
            )}

            {/* ✅ время доступно всегда, а на выходном оно "альтернатива слоту" */}
            <input
              type="time"
              className="form-control mb-3"
              name="startedTime"
              value={form.startedTime}
              onChange={(e) => {
                handleChange(e);
                const v = e.target.value;
                if (dayInfo?.dayOff && v) setSelectedSlotTemplateId("");
              }}
            />

            <select
              disabled={!isTimeReady}
              className="form-control mb-2"
              name="gameRoomId"
              value={form.gameRoomId}
              onChange={handleChange}
            >
              <option value="">Зал</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>

            <label htmlFor="gameTypeSelect" className={common.formLabel}>
              Тип игры
            </label>
            <select id="gameTypeSelect" className="form-control mb-3" value={gameTypeValue} onChange={handleGameTypeChange}>
              <option value="OPEN">Открытая игра</option>
              <option value="CLOSE">Закрытая игра</option>
              <option value="PRIVATE">Игра по заявке</option>
            </select>

            <div className="row mb-3">
              <div className="col-md col-12 mb-md-0 mb-3 row">
                <span className="text-black col-auto d-flex align-items-center">{form.currentCountPlayers} /</span>
                <input
                  type="number"
                  className="form-control col"
                  name="maxCountPlayers"
                  value={form.maxCountPlayers}
                  onChange={handleChange}
                />
                {isEdit && (
                  <span
                    className={`d-flex align-items-center ${common.serviceButton} col-auto`}
                    onClick={() => navigate(`/admin/schedule/${id}/players`)}
                  >
                    <GoPencil />
                  </span>
                )}
              </div>

              <div className="col">
                <input
                  type="number"
                  className="form-control"
                  name="minDurationHours"
                  value={form.minDurationHours}
                  onChange={handleChange}
                  placeholder="Мин. длит. (ч)"
                />
              </div>

              <span className="col-auto p-0 d-flex justify-content-center align-items-center">-</span>

              <div className="col">
                <input
                  type="number"
                  className="form-control"
                  name="maxDurationHours"
                  value={form.maxDurationHours}
                  onChange={handleChange}
                  placeholder="Макс. длит. (ч)"
                />
              </div>
            </div>

            {role !== "MASTER" && (
              <button className="service-btn w-100" onClick={handleSubmit}>
                Сохранить карточку
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
